
import { functions, firestore } from "@/config/firebase";
import { ShoppingCart } from "@/models/events/checkout/shopping-cart.model";
import CardPaymentModal from "@/components/payments/CardPaymentCourse.vue";
import { defineComponent, ref, onMounted, computed, Ref, inject } from "vue";
import { currencyDecimalFilter } from "@/vue-core/filters";
import router from "@/config/router";
import { useRoute } from "vue-router";
import {
  CheckoutCharge,
  Subtotal,
} from "@/models/events/checkout/subtotal.model";
import i18n from "@/i18n";
import { loaderStart, loaderDone } from "@/services/Loader";
import { showMessage } from "@/services/MessageService";
import { useStore } from "vuex";
import useBreakpoints from "@/services/Breakpoints";
export default defineComponent({
  components: {
    AppCardPaymentModal: CardPaymentModal,
  },
  setup() {
    const stripeDialogVisible = ref(false);
    const couponInput: Ref<string | null> = ref("");
    const validCoupong = ref(false);
    const coupongIdApply = ref("");
    const shoppingCart = ref({} as ShoppingCart);
    const stripeAccountId = ref("");
    const stripeClientSecret = ref("");
    const route = useRoute();
    const store = useStore();
    let courseID = ref("");
    const { type } = useBreakpoints();
    const eventData = computed(() => {
      return store.getters["user/eventData"];
    });

    const participantData = computed(() => {
      return store.getters["user/participantData"];
    });
    const applyCoupon = async () => {
      loaderStart();
      const getCoupon = functions.httpsCallable("getStripeCoupon");
      const stripeCouponValidation = functions.httpsCallable(
        "stripeCouponValidation"
      );
      const response = await getCoupon({
        coupongId: couponInput.value,
        eventId: route.params.eventID,
      });
      if (response.data === "not-exist") {
        showMessage({
          title: i18n.global.t("coupon.noExist"),
          message: i18n.global.t("coupon.noExistDetail"),
          type: "warning",
        });
        validCoupong.value = false;
        loaderDone();
      } else if (response.data) {
        const validRedemptions = await stripeCouponValidation({
          coupongId: couponInput.value,
          eventId: route.params.eventID,
          coupongType: "course",
        });
        if (validRedemptions.data !== "valid") {
          showMessage({
            title: i18n.global.t("coupon.invalidCoupon"),
            message: i18n.global.t("coupon.invalidCouponDetail"),
            type: "warning",
          });
          validCoupong.value = false;
          loaderDone();
          return;
        }
        await store.dispatch("program/updateCoursePrice", {
          courseID: courseID.value,
          eventID: route.params.eventID,
          coupon: response.data,
        });
        validCoupong.value = true;
        coupongIdApply.value = response.data.id;
        await getShoppingCart();
        showMessage({
          title: i18n.global.t("coupon.validCoupon"),
          message: i18n.global.t("coupon.validCouponDetail"),
          type: "success",
        });
        loaderDone();
      } else {
        showMessage({
          title: i18n.global.t("coupon.errorCoupon"),
          message: i18n.global.t("coupon.errorCouponDetail"),
          type: "warning",
        });
        loaderDone();
      }
    };

    const coursePriceIsZero = ref(false);

    const getShoppingCart = async () => {
      const getPaymentIntents = functions.httpsCallable("getPaymentIntents");
      const response = (
        await getPaymentIntents({
          eventId: eventData.value.id,
          productType: "course",
        })
      ).data as ShoppingCart;
      if (response === "value-0") {
        coursePriceIsZero.value = true;
        shoppingCart.value = await getShoppingCartWhenPriceZero();
      } else {
        coursePriceIsZero.value = false;
        shoppingCart.value = response;
      }
    };

    const getShoppingCartWhenPriceZero = async () => {
      const cartsList = await firestore("events")
        .doc(eventData.value.id)
        .collection("participants")
        .doc(participantData.value.uid)
        .collection("carts")
        .orderBy("updatedLast", "desc")
        .limit(1)
        .get();
      return cartsList.docs[0].data() as ShoppingCart;
    };

    const formatCurrency = (subtotal: Subtotal) => {
      return currencyDecimalFilter(
        subtotal.amount,
        subtotal.currency,
        i18n.global.locale
      );
    };
    const displayPaymentForm = () => {
      stripeDialogVisible.value = true;
    };
    const preparePaymentForm = (charge: CheckoutCharge) => {
      if (coursePriceIsZero.value) {
        displayPaymentForm();
        return;
      }
      if (
        charge &&
        charge.paymentIntentClientSecret &&
        charge.stripeAccountId
      ) {
        stripeAccountId.value = charge.stripeAccountId;
        stripeClientSecret.value = charge.paymentIntentClientSecret;
        stripeDialogVisible.value = true;
      }
    };

    const updatePaymentStatusWhenPriceIsZero = async () => {
      await store.dispatch("program/registerCourseWithPriceZero", {
        couponID: couponInput.value,
        eventID: route.params.eventID,
        participantID: participantData.value.uid,
      });
    };
    function filterDate(date) {
      let formatDate = new Date();
      if (date._seconds) formatDate = new Date(date._seconds * 1000);
      if (date.seconds) formatDate = new Date(date.seconds * 1000);
      return `${formatDate.getDate()}/${
        formatDate.getMonth() + 1
      }/${formatDate.getFullYear()}`;
    }
    function filterTime(time) {
      let formatTime = new Date();
      if (time._seconds) formatTime = new Date(time._seconds * 1000);
      if (time.seconds) formatTime = new Date(time.seconds * 1000);
      const leadingZero = (num) => `0${num}`.slice(-2);
      return (
        leadingZero(formatTime.getHours()) +
        ":" +
        leadingZero(formatTime.getMinutes())
      );
    }
    function filterSize() {
      if (type.value === "lg") return "30%";
      if (type.value === "md") return "40%";
      return "80%";
    }
    onMounted(async () => {
      const emitter: any = inject("emitter");
      emitter.on("resetCart", () => {
        stripeDialogVisible.value = false;
      });
      loaderStart();
      if (coursePriceIsZero.value) {
        try {
          shoppingCart.value = await getShoppingCartWhenPriceZero();
          loaderDone();
        } catch (error) {
          router.push({ name: "Program" });
          showMessage({
            title: i18n.global.t("program.registerCourse.registerError.title"),
            message: i18n.global.t(
              "program.registerCourse.registerError.message"
            ),
            type: "error",
          });
          loaderDone();
        } finally {
          loaderDone();
        }
      } else {
        try {
          await getShoppingCart();
          if (shoppingCart.value.items && shoppingCart.value.items[0]) {
            courseID.value = shoppingCart.value.items[0].product.id;
          } else {
            router.push({ name: "Program" });
            showMessage({
              title: i18n.global.t(
                "program.registerCourse.registerError.title"
              ),
              message: i18n.global.t(
                "program.registerCourse.registerError.message"
              ),
              type: "error",
            });
          }
        } catch (error) {
          showMessage({
            title: i18n.global.t("program.registerCourse.registerError.title"),
            message: i18n.global.t(
              "program.registerCourse.registerError.message"
            ),
            type: "error",
          });
        } finally {
          loaderDone();
        }
      }
    });

    return {
      shoppingCart,
      stripeDialogVisible,
      stripeAccountId,
      stripeClientSecret,
      eventData,
      participantData,
      coursePriceIsZero,
      getShoppingCart,
      getShoppingCartWhenPriceZero,
      formatCurrency,
      preparePaymentForm,
      displayPaymentForm,
      updatePaymentStatusWhenPriceIsZero,
      couponInput,
      applyCoupon,
      validCoupong,
      filterDate,
      courseID,
      filterTime,
      filterSize,
    };
  },
});
